import React, { useCallback, useState } from 'react';
import { Calculator, Save, RefreshCw, Download, Brain } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import InstitutionParametersSection from './InstitutionParametersSection';
import TechnicalParametersSection from './TechnicalParametersSection';
import TrainingAndSupportSection from './TrainingAndSupportSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import RoiParametersSection from './RoiParametersSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент основной формы расчета стоимости внедрения ИИ
 */
const CalculationForm = ({ 
    formData, 
    setFormData, 
    errors, 
    setErrors, 
    isCalculating, 
    setIsCalculating,
    results,
    setResults,
    costBreakdown,  // Add this parameter
    setCostBreakdown,  // Ensure this is passed
    institutionTypes,
    aiIntegrationLevels,
    infrastructureTypes,
    customizationLevels,
    trainingIntensityLevels,
    maintenanceLevels,
    dataPreparationTypes,
    consultingTypes,
    revenueImpactFactors,
    costReductionFactors,
    regionFactors,
    saveCalculation
  }) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalcName, setSaveCalcName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновление зависимых полей при изменении типа учреждения
      if (name === 'institutionType') {
        const selectedType = institutionTypes.find(type => type.id === fieldValue);
        if (selectedType) {
          newState.studentsCount = selectedType.defaultStudents.toString();
          newState.facultyCount = selectedType.defaultFaculty.toString();
        }
      }

      // Обновление валюты при изменении региона
      if (name === 'region') {
        const selectedRegion = regionFactors.find(region => region.id === fieldValue);
        if (selectedRegion) {
          newState.currency = selectedRegion.currency;
        }
      }

      // Сбросить связанные значения если чекбокс выключен
      if (name === 'includeDataPreparation' && !checked) {
        newState.dataPreparationType = 'partial';
      }
      
      if (name === 'includeConsulting' && !checked) {
        newState.consultingType = 'standard';
      }
      
      if (name === 'includePostDeploymentSupport' && !checked) {
        newState.supportDuration = '12';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [institutionTypes, regionFactors, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      studentsCount, facultyCount, implementationPeriod, aiSolutionsCount,
      supportDuration, existingCostsPerStudent, expectedEfficiencyGain
    } = formData;

    // Парсинг числовых значений
    const numStudentsCount = parseIntOrDefault(studentsCount, -1);
    const numFacultyCount = parseIntOrDefault(facultyCount, -1);
    const numImplementationPeriod = parseIntOrDefault(implementationPeriod, -1);
    const numAiSolutionsCount = parseIntOrDefault(aiSolutionsCount, -1);
    const numSupportDuration = parseIntOrDefault(supportDuration, -1);
    const numExistingCostsPerStudent = parseFloatOrDefault(existingCostsPerStudent, -1);
    const numExpectedEfficiencyGain = parseFloatOrDefault(expectedEfficiencyGain, -1);

    // Валидация количества студентов
    if (!isInteger(studentsCount) || numStudentsCount <= 0) {
      newErrors.studentsCount = 'Введите положительное целое число.';
    }
    if (!newErrors.studentsCount && numStudentsCount > 100000) {
      newErrors.studentsCount = 'Максимальное количество студентов - 100,000.';
    }
    
    // Валидация количества преподавателей
    if (!isInteger(facultyCount) || numFacultyCount <= 0) {
      newErrors.facultyCount = 'Введите положительное целое число.';
    }
    if (!newErrors.facultyCount && numFacultyCount > 10000) {
      newErrors.facultyCount = 'Максимальное количество преподавателей - 10,000.';
    }
    
    // Валидация периода внедрения
    if (!isInteger(implementationPeriod) || numImplementationPeriod <= 0) {
      newErrors.implementationPeriod = 'Введите положительное целое число.';
    }
    if (!newErrors.implementationPeriod && numImplementationPeriod > 60) {
      newErrors.implementationPeriod = 'Максимальный период внедрения - 60 месяцев.';
    }
    
    // Валидация количества ИИ-решений
    if (!isInteger(aiSolutionsCount) || numAiSolutionsCount <= 0) {
      newErrors.aiSolutionsCount = 'Введите положительное целое число.';
    }
    if (!newErrors.aiSolutionsCount && numAiSolutionsCount > 20) {
      newErrors.aiSolutionsCount = 'Максимальное количество ИИ-решений - 20.';
    }
    
    // Валидация длительности поддержки
    if (formData.includePostDeploymentSupport) {
      if (!isInteger(supportDuration) || numSupportDuration < 0) {
        newErrors.supportDuration = 'Введите положительное целое число.';
      }
      if (!newErrors.supportDuration && numSupportDuration > 60) {
        newErrors.supportDuration = 'Максимальная длительность поддержки - 60 месяцев.';
      }
    }
    
    // Валидация существующих затрат на студента
    if (!isPositiveNumber(existingCostsPerStudent)) {
      newErrors.existingCostsPerStudent = 'Введите положительное число.';
    }
    
    // Валидация ожидаемого прироста эффективности
    if (!isPositiveNumber(expectedEfficiencyGain)) {
      newErrors.expectedEfficiencyGain = 'Введите положительное число.';
    }
    if (!newErrors.expectedEfficiencyGain && numExpectedEfficiencyGain > 100) {
      newErrors.expectedEfficiencyGain = 'Значение не может быть больше 100%.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет базовой стоимости на одного студента в зависимости от уровня интеграции
  const calculateBaseStudentCost = (integrationType) => {
    const baseCosts = {
      'basic': 2000, // рублей на студента для базового уровня
      'moderate': 5000,
      'advanced': 12000,
      'comprehensive': 25000
    };
    return baseCosts[integrationType] || 5000;
  };
  
  // Расчет стоимости внедрения и доп. параметров
  const calculateImplementationCost = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setCostBreakdown([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numStudentsCount = parseIntOrDefault(formData.studentsCount, 0);
        const numFacultyCount = parseIntOrDefault(formData.facultyCount, 0);
        const implementationPeriod = parseIntOrDefault(formData.implementationPeriod, 12);
        const numAiSolutionsCount = parseIntOrDefault(formData.aiSolutionsCount, 1);
        const supportDuration = parseIntOrDefault(formData.supportDuration, 12);
        const existingCostsPerStudent = parseFloatOrDefault(formData.existingCostsPerStudent, 0);
        const expectedEfficiencyGain = parseFloatOrDefault(formData.expectedEfficiencyGain, 0) / 100;
        
        // Получаем факторы из выбранных опций
        const selectedInfrastructure = infrastructureTypes.find(i => i.id === formData.infrastructureType) || { costFactor: 1.0 };
        const selectedCustomization = customizationLevels.find(c => c.id === formData.customizationLevel) || { costFactor: 1.0 };
        const selectedTraining = trainingIntensityLevels.find(t => t.id === formData.trainingIntensity) || { costFactor: 1.0 };
        const selectedMaintenance = maintenanceLevels.find(m => m.id === formData.maintenanceLevel) || { costFactor: 1.0 };
        
        const selectedDataPrep = formData.includeDataPreparation ? 
          (dataPreparationTypes.find(d => d.id === formData.dataPreparationType) || { costFactor: 1.0 }) : 
          { costFactor: 0 };
          
        const selectedConsulting = formData.includeConsulting ? 
          (consultingTypes.find(c => c.id === formData.consultingType) || { costFactor: 1.0 }) : 
          { costFactor: 0 };
        
        const selectedRevenueImpact = revenueImpactFactors.find(r => r.id === formData.revenueImpact) || { factor: 0.15 };
        const selectedCostReduction = costReductionFactors.find(c => c.id === formData.costReductionFactor) || { factor: 0.2 };
        
        const selectedRegion = regionFactors.find(r => r.id === formData.region) || { factor: 1.0, currency: 'RUB' };
        
        // Базовая стоимость на студента в зависимости от уровня интеграции
        const baseStudentCost = calculateBaseStudentCost(formData.aiIntegrationLevel);
        
        // --- Расчет основных компонентов стоимости ---
        
        // 1. Базовая стоимость
        const baseCost = baseStudentCost * numStudentsCount;
        
        // 2. Стоимость инфраструктуры
        const infrastructureCost = baseCost * 0.25 * selectedInfrastructure.costFactor * numAiSolutionsCount / 3;
        
        // 3. Стоимость внедрения и кастомизации
        const implementationCost = baseCost * 0.15 * selectedCustomization.costFactor * 
          (implementationPeriod / 12) * Math.sqrt(numAiSolutionsCount);
        
        // 4. Стоимость обучения персонала
        const trainingCost = (numFacultyCount * 15000 + numStudentsCount * 500) * 
          selectedTraining.costFactor * (numAiSolutionsCount / 3);
        
        // 5. Стоимость технической поддержки и обслуживания
        const maintenanceCost = baseCost * 0.12 * selectedMaintenance.costFactor * 
          (formData.includePostDeploymentSupport ? (supportDuration / 12) : 1);
        
        // 6. Стоимость подготовки данных
        const dataPreparationCost = formData.includeDataPreparation ? 
          baseCost * 0.18 * selectedDataPrep.costFactor : 0;
        
        // 7. Стоимость консалтинга
        const consultingCost = formData.includeConsulting ? 
          baseCost * 0.10 * selectedConsulting.costFactor : 0;
        
        // Общая стоимость внедрения
        const totalImplementationCost = (
          baseCost + 
          infrastructureCost + 
          implementationCost + 
          trainingCost + 
          dataPreparationCost + 
          consultingCost
        ) * selectedRegion.factor;
        
        // Ежегодные расходы на поддержку
        const annualMaintenanceCost = maintenanceCost * selectedRegion.factor;
        
        // --- Расчет ROI и экономической эффективности ---
        
        // Общие текущие затраты учреждения на образование
        const totalCurrentCosts = existingCostsPerStudent * numStudentsCount;
        
        // Ожидаемая экономия от внедрения ИИ
        const costSavings = totalCurrentCosts * selectedCostReduction.factor;
        
        // Ожидаемый прирост доходов 
        const additionalRevenue = totalCurrentCosts * expectedEfficiencyGain * selectedRevenueImpact.factor;
        
        // Общий годовой эффект
        const annualBenefit = costSavings + additionalRevenue;
        
        // Период окупаемости (в годах)
        const paybackPeriod = annualBenefit > 0 ? 
          totalImplementationCost / annualBenefit : 0;
        
        // ROI за 3 года (%)
        const threeYearROI = (annualBenefit * 3 - totalImplementationCost - annualMaintenanceCost * 3) / 
          totalImplementationCost * 100;
        
        // --- Подготовка результатов расчета ---
        
        const finalResults = {
          // Общие параметры
          currency: selectedRegion.currency,
          baseStudentCost: baseStudentCost * selectedRegion.factor,
          
          // Стоимостные показатели
          baseCost: baseCost * selectedRegion.factor,
          infrastructureCost: infrastructureCost,
          implementationCost: implementationCost,
          trainingCost: trainingCost,
          maintenanceCost: annualMaintenanceCost,
          dataPreparationCost: dataPreparationCost,
          consultingCost: consultingCost,
          
          totalImplementationCost: totalImplementationCost,
          totalCostPerStudent: totalImplementationCost / numStudentsCount,
          
          // Показатели эффективности
          annualMaintenanceCost: annualMaintenanceCost,
          totalThreeYearCost: totalImplementationCost + annualMaintenanceCost * 3,
          
          costSavings: costSavings,
          additionalRevenue: additionalRevenue,
          annualBenefit: annualBenefit,
          
          paybackPeriod: paybackPeriod,
          threeYearROI: threeYearROI,
          
          // Сравнение с отраслевыми показателями
          industryAvgCostPerStudent: {
            university: 7500,
            school: 4000,
            college: 5500,
            training_center: 6000
          }[formData.institutionType] * selectedRegion.factor,
          
          industryAvgPaybackPeriod: {
            basic: 1.8,
            moderate: 2.5,
            advanced: 3.2,
            comprehensive: 4.0
          }[formData.aiIntegrationLevel]
        };
        
        // Подготовка разбивки затрат
        const costBreakdownData = [
          { 
            category: 'Базовые затраты', 
            cost: finalResults.baseCost, 
            percentage: (finalResults.baseCost / finalResults.totalImplementationCost) * 100,
            description: 'Затраты на основные ИИ-решения и компоненты системы'
          },
          { 
            category: 'Инфраструктура', 
            cost: finalResults.infrastructureCost, 
            percentage: (finalResults.infrastructureCost / finalResults.totalImplementationCost) * 100,
            description: 'Расходы на настройку и поддержку ИТ-инфраструктуры'
          },
          { 
            category: 'Внедрение и кастомизация', 
            cost: finalResults.implementationCost, 
            percentage: (finalResults.implementationCost / finalResults.totalImplementationCost) * 100,
            description: 'Настройка и адаптация ИИ-решений'
          },
          { 
            category: 'Обучение персонала', 
            cost: finalResults.trainingCost, 
            percentage: (finalResults.trainingCost / finalResults.totalImplementationCost) * 100,
            description: 'Подготовка преподавателей и сотрудников к работе с ИИ'
          }
        ];
        
        if (formData.includeDataPreparation) {
          costBreakdownData.push({ 
            category: 'Подготовка данных', 
            cost: finalResults.dataPreparationCost, 
            percentage: (finalResults.dataPreparationCost / finalResults.totalImplementationCost) * 100,
            description: 'Очистка, структурирование и подготовка учебных данных'
          });
        }
        
        if (formData.includeConsulting) {
          costBreakdownData.push({ 
            category: 'Консалтинг', 
            cost: finalResults.consultingCost, 
            percentage: (finalResults.consultingCost / finalResults.totalImplementationCost) * 100,
            description: 'Консультационная поддержка по внедрению и оптимизации процессов'
          });
        }
        
        costBreakdownData.push({ 
          category: 'Ежегодные расходы на поддержку', 
          cost: finalResults.annualMaintenanceCost, 
          percentage: (finalResults.annualMaintenanceCost / finalResults.totalImplementationCost) * 100,
          description: 'Техническое обслуживание, обновления и поддержка системы (в год)'
        });
        
        // Сохранение результатов
        setResults(finalResults);
        setCostBreakdown(costBreakdownData);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные.` });
        setResults(null);
        setCostBreakdown([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setCostBreakdown, setIsCalculating, 
      infrastructureTypes, customizationLevels, trainingIntensityLevels, maintenanceLevels, 
      dataPreparationTypes, consultingTypes, revenueImpactFactors, costReductionFactors, regionFactors]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      institutionType: 'university',
      studentsCount: '5000',
      facultyCount: '300',
      aiIntegrationLevel: 'moderate',
      implementationPeriod: '12',
      
      infrastructureType: 'hybrid',
      aiSolutionsCount: '3',
      existingInfrastructureLevel: 'moderate',
      customizationLevel: 'medium',
      
      trainingIntensity: 'medium',
      maintenanceLevel: 'standard',
      
      includeDataPreparation: true,
      dataPreparationType: 'partial',
      includeConsulting: true,
      consultingType: 'standard',
      includePostDeploymentSupport: true,
      supportDuration: '12',
      
      existingCostsPerStudent: '500',
      expectedEfficiencyGain: '20',
      revenueImpact: 'moderate',
      costReductionFactor: 'medium',
      
      region: 'russia',
      currency: 'RUB',
    });
    setResults(null);
    setCostBreakdown([]);
    setErrors({});
    setSaveCalcName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов расчета в CSV
  const exportToCSV = () => {
    if (!results || !costBreakdown || costBreakdown.length === 0) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Форматирование стоимости для CSV
    const formatCsvCost = (cost) => cost.toFixed(2).replace('.', ',');
    
    // Подготовка заголовков для CSV файла
    const headers = ['Категория', 'Стоимость', 'Процент от общей стоимости', 'Описание'];
    
    // Подготовка данных для CSV файла
    const rows = costBreakdown.map(item => [
      item.category,
      formatCsvCost(item.cost),
      formatCsvCost(item.percentage) + '%',
      item.description
    ]);
    
    // Добавляем итоговую строку
    const totalRow = [
      'ИТОГО:',
      formatCsvCost(results.totalImplementationCost),
      '100,00%',
      'Общая стоимость внедрения ИИ'
    ];
    
    // Добавляем основную информацию в начало файла
    const infoRows = [
      ['Тип учреждения:', getInstitutionTypeName(formData.institutionType)],
      ['Количество студентов:', formData.studentsCount],
      ['Количество преподавателей:', formData.facultyCount],
      ['Уровень интеграции ИИ:', getIntegrationLevelName(formData.aiIntegrationLevel)],
      ['Период внедрения:', `${formData.implementationPeriod} мес.`],
      [''],  // Пустая строка для разделения
      ['Общая стоимость внедрения:', formatCsvCost(results.totalImplementationCost) + ' ' + results.currency],
      ['Стоимость на одного студента:', formatCsvCost(results.totalCostPerStudent) + ' ' + results.currency],
      ['Ежегодные расходы на поддержку:', formatCsvCost(results.annualMaintenanceCost) + ' ' + results.currency],
      ['Срок окупаемости:', results.paybackPeriod.toFixed(1) + ' лет'],
      ['ROI за 3 года:', results.threeYearROI.toFixed(1) + '%'],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows.map(r => r.join(';')), totalRow.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_стоимости_внедрения_ИИ.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Получение названия типа учреждения по ID
  const getInstitutionTypeName = (id) => {
    const institution = institutionTypes.find(type => type.id === id);
    return institution ? institution.name : id;
  };

  // Получение названия уровня интеграции по ID
  const getIntegrationLevelName = (id) => {
    const level = aiIntegrationLevels.find(level => level.id === id);
    return level ? level.name : id;
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalcName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalcName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateImplementationCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Brain className="w-6 h-6 text-indigo-500" />
          Параметры расчета стоимости ИИ
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Параметры учебного заведения */}
        <InstitutionParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          institutionTypes={institutionTypes}
          aiIntegrationLevels={aiIntegrationLevels}
        />

        {/* Технические параметры */}
        <TechnicalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          infrastructureTypes={infrastructureTypes}
          customizationLevels={customizationLevels}
        />

        {/* Обучение и поддержка */}
        <TrainingAndSupportSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          trainingIntensityLevels={trainingIntensityLevels}
          maintenanceLevels={maintenanceLevels}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          dataPreparationTypes={dataPreparationTypes}
          consultingTypes={consultingTypes}
        />

        {/* Параметры расчета ROI */}
        <RoiParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          revenueImpactFactors={revenueImpactFactors}
          costReductionFactors={costReductionFactors}
          regionFactors={regionFactors}
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalcName}
                onChange={(e) => setSaveCalcName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать стоимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {costBreakdown.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;